import type {ComposerAttachment, ComposerImage} from '@github-ui/agent-sessions-interface/components/ChatComposer'
import type {MessageAttachment, MessageEmbeddedResourceAttachment} from '@microsoft/agent-host-protocol'

import {readFileAsBase64} from './read-file-as-base64'
import {IMAGE_MIME_BY_EXTENSION, isAllowedImageMimeType, readImageFile} from './read-image-file'

export const ALLOWED_IMAGE_EXTENSIONS = Object.keys(IMAGE_MIME_BY_EXTENSION)
const ALLOWED_DOCUMENT_MIME_BY_EXTENSION: Record<string, string> = {
  '.pdf': 'application/pdf',
}
export const ALLOWED_DOCUMENT_TYPES = Object.values(ALLOWED_DOCUMENT_MIME_BY_EXTENSION)
export const ALLOWED_DOCUMENT_EXTENSIONS = Object.keys(ALLOWED_DOCUMENT_MIME_BY_EXTENSION)
const PLAIN_TEXT_SAMPLE_BYTES = 8 * 1024

export const MAX_ATTACHMENT_SIZE_BYTES = 32 * 1024 * 1024
export const MAX_ATTACHMENT_SIZE_LABEL = '32 MB'
export const MAX_INLINE_ATTACHMENTS = 10

export type RejectionReason =
  | 'unsupported-type'
  | 'too-large'
  | 'too-many'
  | 'empty'
  | 'unreadable'
  | 'paste-image-only'

export interface RejectedFile {
  name: string
  reason: RejectionReason
}

export interface TrackedAttachment {
  token: ComposerAttachment
  ahp: MessageAttachment
}

interface ProcessUploadedFilesOptions {
  currentUploadCount: number | (() => number)
  createAttachmentId?: () => string
  createImageId: () => string
  readBase64?: typeof readFileAsBase64
  readImage?: typeof readImageFile
}

export interface ProcessedUploadedFiles {
  items: TrackedAttachment[]
  images: ComposerImage[]
  skipped: RejectedFile[]
}

const FILE_NAME_LIST_FORMATTER = new Intl.ListFormat('en', {style: 'long', type: 'conjunction'})
const PASTEABLE_IMAGE_FORMATS = FILE_NAME_LIST_FORMATTER.format(
  [...new Set(Object.values(IMAGE_MIME_BY_EXTENSION))].map(contentType =>
    contentType.slice('image/'.length).toUpperCase(),
  ),
)

function describeFileNames(files: readonly RejectedFile[], maxNames: number): string {
  const displayedFiles = files.slice(0, maxNames)
  const names = displayedFiles.map(file => file.name)
  const remaining = files.length - names.length
  return FILE_NAME_LIST_FORMATTER.format(remaining > 0 ? [...names, `${remaining} more`] : names)
}

const REASON_CLAUSES: ReadonlyArray<{reason: RejectionReason; clause: (names: string, count: number) => string}> = [
  {
    reason: 'unsupported-type',
    clause: (names, count) =>
      `Couldn't attach ${names}. ${count === 1 ? 'This file type is' : 'These file types are'} unsupported.`,
  },
  {
    reason: 'too-large',
    clause: (names, count) =>
      `Couldn't attach ${names}. ${
        count === 1 ? 'This file must' : 'These files must'
      } be ${MAX_ATTACHMENT_SIZE_LABEL} or smaller.`,
  },
  {
    reason: 'too-many',
    clause: names => `Couldn't attach ${names}. You can attach up to ${MAX_INLINE_ATTACHMENTS} documents at a time.`,
  },
  {
    reason: 'empty',
    clause: (names, count) => `Couldn't attach ${names}. ${count === 1 ? 'This file is' : 'These files are'} empty.`,
  },
  {
    reason: 'unreadable',
    clause: (names, count) =>
      `Couldn't attach ${names}. ${count === 1 ? 'This file could' : 'These files could'} not be read.`,
  },
  {
    reason: 'paste-image-only',
    clause: (names, count) =>
      `Couldn't paste ${names}. Only ${PASTEABLE_IMAGE_FORMATS} images can be pasted. Drag in or attach ${
        count === 1 ? 'this file' : 'these files'
      } instead.`,
  },
]

/**
 * Splits one shared soft name budget across the rendered clauses. Every clause keeps at least one name,
 * because a clause reduced to a bare count ("Couldn't attach 1 file") tells the user nothing
 * actionable. The effective floor is the active clause count, so rendered names can exceed maxNames
 * and reach that count when many reasons are present. Spare names go to earlier clauses in reason order.
 */
function allocateNameBudget(groupSizes: readonly number[], maxNames: number): number[] {
  let spare = Math.max(0, maxNames - groupSizes.length)
  return groupSizes.map(size => {
    const extra = Math.min(spare, size - 1)
    spare -= extra
    return 1 + extra
  })
}

export function describeRejections(
  rejected: readonly RejectedFile[],
  {maxNames = 5}: {maxNames?: number} = {},
): string {
  return describeRejectionClauses(rejected, {maxNames}).join(' ')
}

export function describeRejectionClauses(
  rejected: readonly RejectedFile[],
  {maxNames = 5}: {maxNames?: number} = {},
): string[] {
  const groups = REASON_CLAUSES.map(({reason, clause}) => ({
    clause,
    files: rejected.filter(file => file.reason === reason),
  })).filter(group => group.files.length > 0)

  const budgets = allocateNameBudget(
    groups.map(group => group.files.length),
    maxNames,
  )

  return groups.map((group, index) => {
    const budget = budgets[index]
    if (budget === undefined) throw new Error('Rejection name budget does not match the rejection groups')
    return group.clause(describeFileNames(group.files, budget), group.files.length)
  })
}

function extensionFor(file: File): string {
  const extensionStart = file.name.lastIndexOf('.')
  if (extensionStart <= 0 || extensionStart === file.name.length - 1) return ''
  return file.name.slice(extensionStart).toLowerCase()
}

/**
 * Whether the upload path should treat this file as an image at all. Deliberately broader than
 * {@link resolveImageMimeType}: a known image extension routes here even when the declared type
 * contradicts it, so {@link classifyImageSupport} can report that conflict instead of the file
 * silently falling through to the document branch.
 */
export function isAllowedImage(file: File): boolean {
  return isAllowedImageMimeType(file.type) || ALLOWED_IMAGE_EXTENSIONS.includes(extensionFor(file))
}

type ImageSupport = 'accepted' | 'unsupported-type' | 'empty'

export function classifyImageSupport(file: File): ImageSupport {
  if (file.type !== '' && !isAllowedImageMimeType(file.type)) return 'unsupported-type'
  if (file.size === 0) return 'empty'
  return 'accepted'
}

type DocumentSupport = 'accepted' | 'unsupported-type' | 'empty' | 'unreadable'

/**
 * Whether drop and the file picker would accept this file as a document. Mirrors the document
 * branch of `processUploadedFiles` so paste and the shared upload path cannot disagree.
 */
async function classifyDocumentSupport(file: File): Promise<DocumentSupport> {
  if (resolveDocumentMimeType(file)) return file.size === 0 ? 'empty' : 'accepted'
  if (file.size === 0) return file.type.startsWith('text/') ? 'empty' : 'unsupported-type'

  let sample: string
  try {
    sample = await file.slice(0, PLAIN_TEXT_SAMPLE_BYTES).text()
  } catch {
    // Rejecting here would abort the handler before it reports anything.
    return 'unreadable'
  }

  return isPlainText(sample) ? 'accepted' : 'unsupported-type'
}

/**
 * Reason to report for a file the paste path could not take. Paste is image-only, so a file drop or
 * the picker would still accept is reported as paste-only rather than unsupported.
 */
export async function classifyPasteRejection(file: File): Promise<RejectionReason> {
  if (file.size > MAX_ATTACHMENT_SIZE_BYTES) return 'too-large'

  const support = await classifyDocumentSupport(file)
  return support === 'accepted' ? 'paste-image-only' : support
}

function resolveDocumentMimeType(file: File): string | null {
  if (ALLOWED_DOCUMENT_TYPES.includes(file.type)) return file.type
  return ALLOWED_DOCUMENT_MIME_BY_EXTENSION[extensionFor(file)] ?? null
}

function isPlainText(data: string): boolean {
  const sampleSize = Math.min(data.length, 1000)
  if (sampleSize === 0) return true
  let suspiciousCharacters = 0

  for (let i = 0; i < sampleSize; i++) {
    const charCode = data.charCodeAt(i)
    const isDisallowedControlCharacter = charCode < 32 && charCode !== 9 && charCode !== 10 && charCode !== 13
    if (isDisallowedControlCharacter || charCode === 0xfffd) {
      suspiciousCharacters++
    }
  }

  return suspiciousCharacters / sampleSize <= 0.1
}

export async function processUploadedFiles(
  files: readonly File[],
  {
    currentUploadCount,
    createAttachmentId = () => crypto.randomUUID(),
    createImageId,
    readBase64 = readFileAsBase64,
    readImage = readImageFile,
  }: ProcessUploadedFilesOptions,
): Promise<ProcessedUploadedFiles> {
  const items: TrackedAttachment[] = []
  const images: ComposerImage[] = []
  const skipped: RejectedFile[] = []
  const getUploadCount = typeof currentUploadCount === 'function' ? currentUploadCount : () => currentUploadCount

  for (const file of files) {
    if (isAllowedImage(file)) {
      const support = classifyImageSupport(file)
      if (support !== 'accepted') {
        skipped.push({name: file.name, reason: support})
        continue
      }

      const id = createImageId()
      const image = await readImage(file, id, file.name)
      if (image) {
        images.push(image)
      } else {
        skipped.push({name: file.name, reason: 'unreadable'})
      }
      continue
    }

    // Intrinsic problems are reported before the batch limit: too-large and unsupported files stay
    // rejected however many others the user removes, so leading with the count misdirects them.
    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      skipped.push({name: file.name, reason: 'too-large'})
      continue
    }

    const support = await classifyDocumentSupport(file)
    if (support !== 'accepted') {
      skipped.push({name: file.name, reason: support})
      continue
    }

    if (getUploadCount() + items.length >= MAX_INLINE_ATTACHMENTS) {
      skipped.push({name: file.name, reason: 'too-many'})
      continue
    }

    const documentMimeType = resolveDocumentMimeType(file)

    const base64 = await readBase64(file)
    if (!base64) {
      skipped.push({name: file.name, reason: 'unreadable'})
      continue
    }

    const attachmentId = createAttachmentId()
    items.push({
      token: {id: attachmentId, label: file.name, icon: 'file'},
      ahp: {
        type: 'embeddedResource',
        label: file.name,
        data: base64,
        contentType: documentMimeType ?? (file.type || 'text/plain'),
        displayKind: 'document',
        _meta: {'github.attachmentId': attachmentId},
      } as MessageEmbeddedResourceAttachment,
    })
  }

  return {items, images, skipped}
}
